import { type Component, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useReportGeneration } from "@/domains/company/report/context/report-generation-provider";
import {
  PROGRESS_FILL_MS,
  REPORT_PHASES,
} from "@/domains/company/report/utils/report-phases";

/** Sheets drawn in the pile — enough to read as "a stack", not a crowd. */
const SHEETS = 4;

/**
 * The blocking progress dialog shown while the ATS report generates.
 *
 * Visual direction and every timing value come from the validated prototype
 * (variant "Stack"): the pile of CVs is consumed sheet by sheet into the
 * report, with the narrated phase underneath. Two properties of that design are
 * load-bearing and must survive edits:
 *
 * - the text block has a **reserved height** with `truncate`, because the phase
 *   text changes on a timer and a resizing card would make the modal jump;
 * - the sheet's travel is a **single `translate` curve** while `transform`
 *   handles lift/scale/opacity separately. Splitting them is what removed the
 *   stutter: intermediate keyframes on the path create seams where velocity
 *   jumps (measured 3.2x between frames; 1.29x after the split).
 *
 * Not dismissible: the request cannot be aborted, so letting the user close
 * this would hide an operation that is still running and deliver a PDF out of
 * nowhere. Escape, outside-click and the built-in X are all suppressed.
 */
export const ReportGenerationDialog: Component = () => {
  const { t } = useI18n();
  const generation = useReportGeneration();
  const key = "profile.company.ats.report.loader";

  const open = () =>
    generation.activeOffer() !== null && !generation.isMinimized();

  const phase = () => REPORT_PHASES[generation.phaseIndex()];
  const progress = () => generation.progress();
  /* "Arrivato" è esattamente barra piena: il provider porta il progresso a 1
     solo quando il PDF è in mano, mai su un orologio. */
  const isComplete = () => progress() >= 1;

  return (
    <Dialog open={open()}>
      <DialogContent
        hideCloseButton
        class="max-w-[400px] gap-0"
        onEscapeKeyDown={(event: KeyboardEvent) => event.preventDefault()}
        onPointerDownOutside={(event: Event) => event.preventDefault()}
        onInteractOutside={(event: Event) => event.preventDefault()}
        aria-label={t(`${key}.title`) as string}
      >
        <div class="relative h-[128px]">
          {/* The path the sheets travel, so the motion reads as "along here". */}
          <div class="report-loader-rail absolute top-[46px] right-[64px] left-[76px] h-px" />

          {/* Source: the pile of CVs. */}
          <div class="absolute top-0 left-0 w-[92px]">
            <div class="relative h-[76px]">
              <For each={Array.from({ length: SHEETS })}>
                {(_, i) => (
                  <div
                    class="report-loader-sheet absolute top-0 left-0 h-[68px] w-[52px] rounded-md border border-border bg-background"
                    style={{
                      transform: `translate(${i() * 6}px, ${i() * 3}px)`,
                      "z-index": String(SHEETS - i()),
                    }}
                  >
                    <div class="flex flex-col gap-1 p-2">
                      <div class="h-1 w-5 rounded-full bg-muted-foreground/30" />
                      <div class="h-1 w-full rounded-full bg-muted" />
                      <div class="h-1 w-4/5 rounded-full bg-muted" />
                      <div class="h-1 w-full rounded-full bg-muted" />
                    </div>
                  </div>
                )}
              </For>
            </div>
          </div>

          {/* The sheet in flight: pile → report. */}
          <div class="report-loader-flying absolute top-0 left-[76px] h-[68px] w-[52px] rounded-md border border-primary/50 bg-card shadow-sm">
            <div class="flex flex-col gap-1 p-2">
              <div class="h-1 w-5 rounded-full bg-primary/50" />
              <div class="h-1 w-full rounded-full bg-muted" />
              <div class="h-1 w-4/5 rounded-full bg-muted" />
              <div class="h-1 w-full rounded-full bg-muted" />
            </div>
            <i class="ph-fill ph-sparkle report-loader-mark absolute -top-1.5 -right-1.5 text-xs text-primary" />
          </div>

          {/* Destination: the report being assembled. */}
          <div class="absolute top-0 right-0 w-[76px]">
            <div class="flex h-[76px] items-start justify-center">
              <div class="report-loader-target flex size-14 items-center justify-center rounded-xl bg-primary">
                <i class="ph-fill ph-file-text text-2xl text-primary-foreground" />
              </div>
            </div>
            <p class="mt-1 text-center text-[11px] font-medium text-muted-foreground">
              {t(`${key}.target`)}
            </p>
          </div>
        </div>

        {/* Reserved height: the phase text changes on a timer, and a resizing
            card would make the modal jump. */}
        <div class="mt-5 flex h-[92px] flex-col items-center justify-start text-center">
          {/* La barra: nessun conteggio di documenti, solo il tempo trascorso e
              l'arrivo del PDF. Si completa sotto gli occhi dell'utente prima
              che il dialog si chiuda — il provider aspetta il riempimento. */}
          <div class="w-full px-1">
            <div class="mb-1.5 flex items-baseline justify-between">
              <span class="text-xs font-medium text-muted-foreground">
                {isComplete() ? t(`${key}.ready`) : t(`${key}.in_progress`)}
              </span>
              <span class="text-sm font-semibold tabular-nums">
                <Show
                  when={isComplete()}
                  fallback={`${Math.round(progress() * 100)}%`}
                >
                  <span class="inline-flex items-center gap-1 text-primary">
                    <i class="ph-fill ph-check-circle text-base" />
                    100%
                  </span>
                </Show>
              </span>
            </div>
            <div
              class="h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={Math.round(progress() * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                class="h-full rounded-full bg-primary"
                style={{
                  width: `${progress() * 100}%`,
                  /* `linear` durante la corsa (moto costante); ease-out sul
                     tratto finale, che è un'entrata e deve leggersi come una
                     chiusura decisa. */
                  transition: isComplete()
                    ? `width ${PROGRESS_FILL_MS}ms var(--ease-out-strong)`
                    : "width 200ms linear",
                }}
              />
            </div>
          </div>
          {/* Announced politely so screen readers follow the narration without
              the focus being yanked around on every phase change. */}
          <div aria-live="polite" aria-atomic="true" class="w-full">
            <Show when={phase()}>
              <h3 class="mt-3 w-full truncate text-sm font-semibold leading-snug">
                {t(`${key}.phases.${phase().key}.label` as never) as string}
              </h3>
              <p class="mt-1 w-full truncate text-xs text-muted-foreground">
                {t(`${key}.phases.${phase().key}.detail` as never) as string}
              </p>
            </Show>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
